import {
  Box,
  Container,
  CssBaseline,
  ThemeProvider,
  Typography,
  useMediaQuery,
  styled,
} from "@mui/material";
import theme from "../../../config/theme";
import icon from "../../../assets/icon.png";
import Ledgerhome from "../../../assets/Ledgerhome.png";
import Dockethome from "../../../assets/Dockethome.png";
import Bitehome from "../../../assets/Bitehome.png";
import Group31 from "../../../assets/Group31.png";

// Styled components
const ProductCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "1.5rem",
  background: "rgba(255, 255, 255, 0.9)",
  borderRadius: "16px",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(99, 102, 241, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.2)",
  },
}));

const ProductImage = styled("img")({
  width: "100px",
  height: "100px",
  objectFit: "contain",
  marginBottom: "1rem",
});

const ProductTitle = styled(Typography)({
  fontFamily: '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 700,
  fontSize: "1.1rem",
  color: "#1F2937",
  textAlign: "center",
  marginBottom: "0.5rem",
});

const ValueProposition = styled(Typography)({
  fontFamily: '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 800,
  fontSize: "2.5rem",
  background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #F59E0B 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
  marginBottom: "0.5rem",
  animation: "gradientShift 3s ease-in-out infinite",
});

const HowItWorks = styled(Typography)({
  fontFamily: '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 400,
  fontSize: "1.2rem",
  color: "#6B7280",
  textAlign: "center",
  marginBottom: "1rem",
  lineHeight: 1.6,
});

const ProductDescription = styled(Typography)({
  fontFamily: '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 500,
  fontSize: "0.9rem",
  color: "#4B5563",
  textAlign: "center",
  marginTop: "0.5rem",
  lineHeight: 1.4,
});

const CentralLogo = styled("img")({
  width: "120px",
  height: "120px",
  objectFit: "contain",
  animation: "pulse 2s ease-in-out infinite",
  filter: "drop-shadow(0 8px 16px rgba(99, 102, 241, 0.3))",
  zIndex: 10,
  position: "relative",
});

const ConnectionLine = styled(Box)({
  position: "absolute",
  height: "2px",
  background: "linear-gradient(90deg, #6366F1, #8B5CF6, #F59E0B, #10B981)",
  borderRadius: "1px",
  opacity: 0.6,
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
    animation: "shimmer 2s ease-in-out infinite",
  },
});

const Landing = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const products = [
    {
      name: "LEDGER",
      image: Ledgerhome,
      description: "Financial Management",
    },
    {
      name: "DOCKET", 
      image: Dockethome,
      description: "Student Records",
    },
    {
      name: "BITE",
      image: Bitehome,
      description: "Canteen Operations",
    },
    {
      name: "ANALYTICS",
      image: Group31,
      description: "Performance Insights",
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        background: "linear-gradient(135deg, #EEF6FF 0%, #F0F9FF 50%, #E0F2FE 100%)",
        position: "relative",
        overflow: "hidden",
        minHeight: isMobile ? "800px" : "900px",
        padding: isMobile ? "4rem 0" : "5rem 0",
        display: "flex",
        alignItems: "center",
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.1) 0%, transparent 50%)",
          pointerEvents: "none",
        }
      }}>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, marginBottom: "2rem" }}>
          {isMobile ? (
            // Mobile Layout
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center",
              gap: "1.5rem",
              marginBottom: "2rem"
            }}>
              {/* Value Proposition */}
              <ValueProposition sx={{ fontSize: "1.8rem" }}>
                All-in-One Education Platform
              </ValueProposition>
              
              <HowItWorks sx={{ fontSize: "1rem" }}>
                Four integrated modules working together to streamline your institution
              </HowItWorks>
              
              {/* Products Grid with Central Logo */}
              <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gridTemplateRows: "repeat(3, 1fr)",
                gap: "1rem",
                width: "100%",
                maxWidth: "450px",
                height: "450px",
                position: "relative"
              }}>
                {/* Central Logo positioned in center (grid position 2,2) */}
                <Box sx={{
                  gridColumn: "2",
                  gridRow: "2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 5,
                  width: "80px",
                  height: "80px",
                  background: "rgba(255, 255, 255, 0.9)",
                  borderRadius: "50%",
                  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                  margin: "auto"
                }}>
                  <img 
                    src={icon} 
                    alt="Youstad Logo"
                    style={{ width: "50px", height: "50px", objectFit: "contain" }}
                  />
                </Box>
                
                {/* Product Cards positioned around the center */}
                {products.map((product, index) => {
                  const positions = [
                    { gridColumn: "1", gridRow: "1" }, // Top-left
                    { gridColumn: "3", gridRow: "1" }, // Top-right
                    { gridColumn: "1", gridRow: "3" }, // Bottom-left
                    { gridColumn: "3", gridRow: "3" }  // Bottom-right
                  ];
                  
                  return (
                    <Box key={index} sx={{ 
                      ...positions[index],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <ProductCard sx={{ width: "100%", maxWidth: "120px" }}>
                        <ProductImage 
                          src={product.image} 
                          alt={product.name}
                          style={{ width: "60px", height: "60px" }}
                        />
                        <ProductTitle sx={{ fontSize: "0.9rem" }}>
                          {product.name}
                        </ProductTitle>
                        <ProductDescription sx={{ fontSize: "0.7rem" }}>
                          {product.description}
                        </ProductDescription>
                      </ProductCard>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            // Desktop Layout
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center",
              gap: "2rem",
              marginBottom: "2rem"
            }}>
              {/* Value Proposition */}
              <ValueProposition>
                All-in-One Education Platform
              </ValueProposition>
              
              <HowItWorks>
                Four integrated modules working together to streamline your institution
              </HowItWorks>
              
              {/* Products in a circle around the logo */}
              <Box sx={{ 
                position: "relative",
                width: "650px",
                height: "650px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {/* Central Logo positioned lower */}
                <Box sx={{
                  position: "absolute",
                  top: "55%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 10,
                  width: "120px",
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "50%",
                  boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
                  border: "2px solid rgba(99, 102, 241, 0.1)",
                }}>
                  <img 
                    src={icon} 
                    alt="Youstad Logo"
                    style={{ width: "70px", height: "70px", objectFit: "contain" }}
                  />
                </Box>
                {/* Connection Lines */}
                <ConnectionLine sx={{
                  width: "200px",
                  top: "55%",
                  left: "50%",
                  transform: "translate(-50%, -50%) rotate(45deg)",
                }} />
                <ConnectionLine sx={{
                  width: "200px", 
                  top: "55%",
                  left: "50%",
                  transform: "translate(-50%, -50%) rotate(-45deg)",
                }} />
                <ConnectionLine sx={{
                  width: "200px",
                  top: "55%",
                  left: "50%", 
                  transform: "translate(-50%, -50%) rotate(135deg)",
                }} />
                <ConnectionLine sx={{
                  width: "200px",
                  top: "55%",
                  left: "50%",
                  transform: "translate(-50%, -50%) rotate(-135deg)",
                }} />
                
                {/* Product Cards positioned around the center */}
                {products.map((product, index) => {
                  const angle = (index * 90) * (Math.PI / 180);
                  const radius = 200;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  
                  return (
                    <Box
                      key={index}
                      sx={{
                        position: "absolute",
                        left: `calc(50% + ${x}px - 80px)`,
                        top: `calc(50% + ${y}px - 80px)`,
                        width: "160px",
                      }}
                    >
                      <ProductCard>
                        <ProductImage 
                          src={product.image} 
                          alt={product.name}
                          style={{ width: "80px", height: "80px" }}
                        />
                        <ProductTitle>{product.name}</ProductTitle>
                        <ProductDescription sx={{ fontSize: "0.8rem" }}>
                          {product.description}
                        </ProductDescription>
                      </ProductCard>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Landing;
